import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import RatingSummary from "@/components/ratings/RatingSummary";
import { API } from "@/lib/api";

export default function CandidateCard({ candidate }) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/candidates/${candidate.user_id}`}
      className="block bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 hover:-translate-y-1 hover:border-black/10 transition-all duration-200"
      data-testid={`candidate-card-${candidate.user_id}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-full bg-[#FAFAFA] border border-black/10 overflow-hidden shrink-0 flex items-center justify-center">
          {candidate.photo_path ? (
            <img src={`${API}/profiles/files/${candidate.photo_path}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-[#4ba3b7]">{candidate.first_name?.[0]}</span>
          )}
        </div>
        <div>
          <h3 className="font-heading font-bold">{candidate.first_name} {candidate.last_name}</h3>
          {candidate.nationality && (
            <p className="text-xs text-[#2B2B2B]/50">{t(`lookups.nationalities.${candidate.nationality}`)}</p>
          )}
        </div>
        <div className="ml-auto">
          <RatingSummary userId={candidate.user_id} testId={`candidate-card-rating-${candidate.user_id}`} />
        </div>
      </div>

      {candidate.is_tesa_ready && (
        <Badge className="mb-3 bg-[#34C759]/10 text-[#34C759] border-0 rounded-full text-xs gap-1" data-testid={`candidate-card-tesa-badge-${candidate.user_id}`}>
          <ShieldCheck className="h-3 w-3" /> {t("candidateProfile.tesaReadyBadge")}
        </Badge>
      )}

      {candidate.countries_wanted?.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-[#2B2B2B]/60 mb-3">
          <MapPin className="h-4 w-4 shrink-0" />
          {candidate.countries_wanted.map((c) => t(`lookups.countries.${c}`)).join(", ")}
        </div>
      )}

      {candidate.mission_preferences?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`candidate-card-mission-prefs-${candidate.user_id}`}>
          {candidate.mission_preferences.map((pref, idx) => pref.region && (
            <Badge key={idx} variant="outline" className="rounded-full text-xs border-[#dde165]/60 bg-[#dde165]/10">
              {t(`lookups.regions.${pref.region}`)}
            </Badge>
          ))}
        </div>
      )}

      {(candidate.worker_status || candidate.rate_type) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {candidate.worker_status && (
            <Badge variant="outline" className="rounded-full text-xs border-[#4ba3b7]/30 text-[#4ba3b7]">
              {t(`lookups.workerStatuses.${candidate.worker_status}`)}
            </Badge>
          )}
          {candidate.rate_type && candidate.rate_amount != null && (
            <Badge variant="outline" className="rounded-full text-xs">
              {candidate.rate_amount}&euro; / {t(`lookups.rateTypes.${candidate.rate_type}`)}
            </Badge>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(candidate.skills || []).slice(0, 3).map((s) => (
          <Badge key={s} variant="outline" className="rounded-full text-xs">{t(`lookups.skills.${s}`)}</Badge>
        ))}
      </div>
    </Link>
  );
}
